import React, {Component} from 'react'
import cn from "classnames"
import first from "lodash/first"
import orderBy from "lodash/orderBy"
import injectSheet from 'react-jss'
import {UnstyledLink} from "../Widgets/Link"
import theme, {BASE_PIXELS} from "../../styles/theme"
import get from "lodash/get"
import {round} from "../../utils/math"

const default_image = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

const getViewportAspect = (images, breakpointName) => {
    const img = first( get(images, [breakpointName]) );
    if( ! img ) return null;
    const aspect = img.viewportAspect ? img.viewportAspect : img.dimensions.aspect;
    return `${round(100 / aspect)}%`
}

const ImageElement = ({alt, className, onMouseEnter, onMouseLeave, onLoad}) => ( <img data-sizes="auto"
                                       alt={alt}
                                       className={className}
                                       src={default_image}
                                       ref={this._setRef}
                                       onMouseEnter={onMouseEnter}
                                       onMouseLeave={onMouseLeave}
                                       onLoad={onLoad}/> );

class ResponsiveImage extends Component {

    shouldComponentUpdate(nextProps, nextState) {
        // only change if the images change
        const {images, children, className, classes, hover} = this.props;
        if(get(images, ['desktop', 0, 'url']) !== get(nextProps.images, ['desktop', 0, 'url'])) {
            return true;
        }
        if(children !== nextProps.children ||
            className !== nextProps.className ||
            classes !== nextProps.classes ||
            hover !== nextProps.hover) {
            return true;
        }

        return false;
    }

    _setRef = (ref) => {
        const {setRef} = this.props;
        this._ref = ref;
        if(setRef) setRef(ref);
    }

    render() {
        const {images, classes, className, link, children, aspect, blurEnabled=true,
          onMouseEnter, onMouseLeave, dataCy = 'responsiveImageContainer' } = this.props;
        if(!images) {
          return null
        }
        const mainImage = first(images.desktop);
        const blurImage = blurEnabled && first(images.blur);
        let sources = [];

        for (var property in images ) {
            if (!images.hasOwnProperty(property) ||
                !Array.isArray(images[property]) ||
                property === "blur" ||
                property === "video") continue;

            var webPImages = images[property].filter(x => x.type === 'image/webp')
            var otherImages = images[property].filter(x => x.type !== 'image/webp')

            if( webPImages && webPImages.length > 0)
            {
                sources.push( {
                    breakpoint: theme.breakpoints[property],
                    element: <source key={sources.length}
                                     data-srcset={webPImages.map(item => (`${item.url} ${item.dimensions.width}w`))}
                                     type='image/webp'
                                     media={property !== "desktop" ? `(max-width: ${((theme.breakpoints[property]-1)/BASE_PIXELS)}rem)` : ""}/>
                });
            }
            sources.push( {
                breakpoint: theme.breakpoints[property],
                element: <source key={sources.length}
                                 data-srcset={otherImages.map(item => (`${item.url} ${item.dimensions.width}w`))}
                                 media={property !== "desktop" ? `(max-width: ${((theme.breakpoints[property]-1)/BASE_PIXELS)}rem)` : ""}/>
            });

        }

        sources = orderBy(sources, "breakpoint", "asc");

        const imageCss = cn({
            [classes.blurUp]: blurImage,
            lazyload: true,
            [classes.image]: true
        });

        const responsiveImage = <picture>
            {/*<!--[if IE 9]><video style="display: none;"><![endif]-->*/}
            {sources.map(x => x.element)}
            <ImageElement alt={mainImage ? mainImage.alt : ""}
                          className={imageCss}
                          onMouseEnter={onMouseEnter}
                          onMouseLeave={onMouseLeave}
                          />
            {/*<!--[if IE 9]></video><![endif]-->*/}
        </picture>;

        const hasLink = link && link.url;

        return <div className={cn( classes.container, {
                [classes.containerHoverEffect]: hasLink,
                [classes.dynamicAspect]: !aspect,
                [classes.fixedAspect]: aspect,
            }, className )}
            ref={this._setRef}
            data-cy={dataCy}>
                {blurImage && <img key="blur" src={blurImage.data ? blurImage.data : blurImage.url} data-cy="responsiveImageBlurred" className={cn(classes.imageBlur, 'blur')}
                                   alt={mainImage ? mainImage.alt : ""}/>}
                {hasLink ?
                    <UnstyledLink className={classes.link} link={link}>{responsiveImage}</UnstyledLink> :
                    responsiveImage
                }
                {children}
            </div>
    }
}

const BLUR_PIXELS = 10;

export default injectSheet((theme) => ({
    container: {
        position: 'relative',
        width: "100%",
        display: 'block',
        overflow: "hidden",
        marginLeft: "auto",
        marginRight: "auto",
        '&::before': {
            display: 'block',
            content: '""',
        },
    },
    fixedAspect: {
        '&::before': {
            paddingTop: ({aspect}) => `${round(100 * aspect)}%`,
        }
    },
    dynamicAspect: {
        '&::before': {
            paddingTop: ({images}) => getViewportAspect(images, 'desktop'),
            [theme.breakpoint("laptop", "max")]: {
                paddingTop: ({images}) => getViewportAspect(images, 'laptop')
            },
            [theme.breakpoint("tablet", "max")]: {
                paddingTop: ({images}) => getViewportAspect(images, 'tablet')
            },
            [theme.breakpoint("mobile", "max")]: {
                paddingTop: ({images}) => getViewportAspect(images, 'mobile')
            }
        }
    },
    image: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        transition: "all 0.3s",
        objectFit: "cover",
        fontFamily: '"object-fit: cover;"', // object-fit polyfill
    },
    imageBlur: {
        position: "absolute",
        top: `0`,
        left: `0`,
        width: `100%`,
        height: `100%`,
        filter: `blur(${BLUR_PIXELS}px)`,
        objectFit: "cover",
        fontFamily: '"object-fit: cover;"', // object-fit pollyfill
        transform: "scale(1.1)"
    },
    blurUp: {
        //filter: `blur(${BLUR_PIXELS}px)`,
        opacity: "0",
        transition: "filter 400ms, opacity 400ms",
        "&.lazyloaded": {
            //filter: "blur(0)",
            opacity: "1",
        },
    },
    link: {
        textDecoration: "none",
    }
}))(ResponsiveImage)



// WEBPACK FOOTER //
// ./src/components/Widgets/ResponsiveImage.js